import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { MomentFormPageTabs } from "../../../pages/moments/moment-form-page-tabs";
import { useFlag } from "../../../server/optimizely";
import { PageWithFooterWrapper } from "../../generic/components/page-with-footer-wrapper";
import { Page } from "../../layout/components/page";
import { useRecipientsForChannel } from "../../recipients/hooks/use-recipients-for-channel";
import type { RecipientPerson } from "../../recipients/server/supabase-recipient-service";
import type { ConnectionCategory } from "../hooks/use-connections";
import { useMergeTags } from "../hooks/use-merge-tags";
import { useMomentPageMetadata } from "../hooks/use-moment-page-metadata";
import type { MomentRestrictions } from "../hooks/use-moment-restrictions";
import { usePrograms } from "../hooks/use-programs";
import { type MomentFormYupContext, momentSchema } from "../types";
import type { MomentFormMoment } from "../types/moment-form-moment";
import { FullWidthMomentFormHeader } from "./full-width-moment-form-header";
import { MomentNewProvider } from "./moment-new-context";

export type MomentFormArgs = {
  moment: MomentFormMoment;
  reloadMoment: () => void;
  isNewsletter?: boolean;
  connectionCategories: ConnectionCategory[];
  momentRestrictions: MomentRestrictions;
};

export const MomentForm: FC<
  Omit<MomentFormArgs, "moment"> & { moment: MomentFormMoment & { isNew: boolean } }
> = ({
  moment: { isNew, ...moment },
  connectionCategories,
  momentRestrictions,
  isNewsletter,
  ...rest
}) => {
  const [reviewExperienceEnabled] = useFlag("reviewer_role");
  const [persistedMomentId, setPersistedMomentId] = useState(isNew ? null : moment.id);
  const currentIsNew = isNew && persistedMomentId !== moment.id;
  const displayedIsNew = reviewExperienceEnabled ? currentIsNew : isNew;

  const metadata = useMomentPageMetadata(moment, isNewsletter);

  const {
    data: mergeTags,
    reload: reloadMergeTags,
    error: mergeTagError,
    loading: mergeTagsLoading,
  } = useMergeTags();
  const programs = usePrograms();
  const [validRecipients, setValidRecipients] = useState<RecipientPerson[]>([]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore ts(2589)
  const methods = useForm<MomentFormMoment, MomentFormYupContext>({
    defaultValues: moment,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-expect-error
    resolver: yupResolver(momentSchema),
    reValidateMode: "onChange",
    context: {
      mergeTags: mergeTags ?? [],
      validate_additional_recipients: true,
      validRecipients,
      connectionCategories,
    },
  });

  const { control, getValues } = methods;
  useWatch({ control, name: ["channel", "target_channel", "notify_channel"] });

  const [channel, target_channel, notify_channel] = getValues([
    "channel",
    "target_channel",
    "notify_channel",
  ]);

  const {
    data: recipients,
    loading: recipientsLoading,
    error: recipientsError,
  } = useRecipientsForChannel(channel, notify_channel || target_channel);

  const saveDataReady =
    !mergeTagsLoading &&
    !programs.loading &&
    !recipientsLoading &&
    !mergeTagError &&
    !programs.error &&
    !recipientsError;

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setValidRecipients(recipients ?? []), [recipients]);

  if (mergeTagError) return <></>;
  return (
    <MomentNewProvider value={displayedIsNew}>
      <FormProvider {...methods}>
        <Page {...metadata}>
          <FullWidthMomentFormHeader
            moment={{ isNew: displayedIsNew, ...moment }}
            reloadMoment={rest.reloadMoment}
            isNewsletter={isNewsletter}
            programs={programs}
          />

          <PageWithFooterWrapper>
            <Stack
              direction="column"
              width="100%"
              alignContent="center"
              alignItems={reviewExperienceEnabled ? undefined : "center"}
              sx={reviewExperienceEnabled ? undefined : { px: 2 }}
            >
              <Stack
                direction="column"
                width="100%"
                maxWidth={reviewExperienceEnabled ? undefined : 1440}
              >
                <MomentFormPageTabs
                  moment={moment}
                  isNew={displayedIsNew}
                  reviewExperienceEnabled={reviewExperienceEnabled}
                  onSaved={() => setPersistedMomentId(moment.id)}
                  mergeTags={mergeTags ?? []}
                  reloadMergeTags={reloadMergeTags}
                  connectionCategories={connectionCategories ?? []}
                  momentRestrictions={momentRestrictions}
                  isNewsletter={isNewsletter}
                  saveDataReady={saveDataReady}
                  programs={programs}
                  {...rest}
                />
              </Stack>
            </Stack>
          </PageWithFooterWrapper>
        </Page>
      </FormProvider>
    </MomentNewProvider>
  );
};
